import { ProjectStudyCoordinator } from "../components/ProjectStudyCoordinator";
import { SnapShotCoordinator } from "../components/desktop/SnapShotCoordinator";
import { type ServerLifecycleWelcomePayload } from "@afar-research/contracts";
import { scopedProjectKey, scopeProjectRef } from "@afar-research/client-runtime/environment";
import { squashAtomCommandFailure } from "@afar-research/client-runtime/state/runtime";
import {
  Outlet,
  createRootRoute,
  type ErrorComponentProps,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import * as Schema from "effect/Schema";

import { applyAppearanceContrast } from "../appearanceContrast";
import {
  applyAppearanceFontVariables,
  resolveTerminalFontPreference,
  resolveTerminalFontSizePreference,
  TYPOGRAPHY_ADVANCED_STORAGE_KEY,
} from "../appearanceFonts";
import { useDefaultThemeAdoption } from "../hooks/useDefaultTheme";
import { useEnvironmentThemeSync } from "../hooks/useEnvironmentTheme";
import { useLocalStorage } from "../hooks/useLocalStorage";

import { APP_DISPLAY_NAME } from "../branding";
import { WorkspaceShell } from "../components/WorkspaceShell";
import { CommandPaletteShell } from "../components/CommandPaletteShell";
import { CloudWorkspaceConnections } from "../connection/CloudWorkspaceConnections";
import { AccountEntryGate } from "../components/AccountEntryGate";
import { browserBuildEnvironment } from "../env";
import { requiresBrowserAccount } from "../environments/primary/browserEntry.logic";
import { SlowRpcRequestToastCoordinator } from "../components/SlowRpcRequestToastCoordinator";
import { ProjectActionReadinessToastCoordinator } from "../components/ProjectActionReadinessToastCoordinator";
import { AppErrorView } from "../components/AppErrorView";
import { useThreadNotifications } from "../hooks/useThreadNotifications";
import {
  AnchoredToastProvider,
  stackedThreadToast,
  ToastProvider,
  toastManager,
} from "../components/ui/toast";
import { resolveAndPersistPreferredEditor } from "../editorPreferences";
import { useClientSettings } from "../hooks/useSettings";
import { ThemeEditorHost } from "../components/settings/ThemeEditorHost";
import { CustomSnoozeDialogHost } from "../components/CustomSnoozeDialog";
import { FeedbackDialogHost } from "../components/settings/FeedbackDialog";
import {
  deriveLogicalProjectKeyFromSettings,
  derivePhysicalProjectKeyFromPath,
  selectProjectGroupingSettings,
} from "../logicalProject";
import { useUiStateStore } from "../uiStateStore";
import { syncBrowserChromeTheme } from "../hooks/useTheme";
import { shellEnvironment } from "../state/shell";
import { useAtomValue } from "@effect/atom-react";
import { useAtomCommand } from "../state/use-atom-command";
import { usePrimaryEnvironment } from "../state/environments";
import { useRouteEnvironmentId } from "../hooks/useRouteEnvironmentId";
import {
  primaryServerConfigAtom,
  primaryServerConfigEventAtom,
  primaryServerWelcomeAtom,
} from "../state/server";
import { readProject } from "../state/entities";
import { ThreadDiscoveryProvider } from "../threadDiscoveryContext";
import {
  createKeybindingsUpdateToastController,
  type KeybindingsUpdateToastController,
} from "../components/KeybindingsUpdateToast.logic";
import { commandFailureMessage } from "~/rpc/transportError";

export const Route = createRootRoute({
  component: RootRouteView,
  errorComponent: RootRouteErrorView,
  head: () => ({
    meta: [{ name: "title", content: APP_DISPLAY_NAME }],
  }),
});

function RootRouteView() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const routeEnvironmentId = useRouteEnvironmentId();

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      syncBrowserChromeTheme();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [pathname]);

  const appShell = (
    <ThreadDiscoveryProvider>
      <CloudWorkspaceConnections selectedEnvironmentId={routeEnvironmentId} />
      <CommandPaletteShell>
        <WorkspaceShell>
          <Outlet />
        </WorkspaceShell>
        <ProjectStudyCoordinator />
      </CommandPaletteShell>
    </ThreadDiscoveryProvider>
  );

  return (
    <ToastProvider>
      <AnchoredToastProvider>
        <DocumentTitleSync />
        <EnvironmentThemeSync />
        <ContrastAppearanceSync />
        <GlassAppearanceSync />
        <FontAppearanceSync />
        <SnapShotCoordinator />
        <ThreadNotificationCoordinator />
        <SlowRpcRequestToastCoordinator />
        <ProjectActionReadinessToastCoordinator />
        <EventRouter />
        {/* Only the Runtime Web Client admits by account; its app waits for a usable one. */}
        {requiresBrowserAccount(browserBuildEnvironment) ? (
          <AccountEntryGate>{appShell}</AccountEntryGate>
        ) : (
          appShell
        )}
        {/* Above the router: a theme draft is judged by walking the app, so the
            editor has to survive navigation away from settings. */}
        <ThemeEditorHost />
        {/* Any surface can offer a custom snooze; the dialog it awaits lives
            here so no single menu has to own it. */}
        <CustomSnoozeDialogHost />
        <FeedbackDialogHost />
      </AnchoredToastProvider>
    </ToastProvider>
  );
}

/** Follows the palette the primary environment's machine publishes, if any. */
function EnvironmentThemeSync() {
  useEnvironmentThemeSync();
  // Ordered after the palette sync so a first-run client adopting the
  // environment's own theme finds it already in the library.
  useDefaultThemeAdoption();
  return null;
}

function ContrastAppearanceSync() {
  const appearanceContrast = useClientSettings((settings) => settings.appearanceContrast);
  const diffColorScheme = useClientSettings((settings) => settings.diffColorScheme);

  useEffect(() => {
    document.documentElement.dataset.diffColorScheme = diffColorScheme;
  }, [diffColorScheme]);

  useEffect(() => {
    applyAppearanceContrast(document.documentElement, appearanceContrast);
  }, [appearanceContrast]);

  return null;
}

function GlassAppearanceSync() {
  const glassOpacity = useClientSettings((settings) => settings.glassOpacity);

  useEffect(() => {
    const style = document.documentElement.style;
    style.setProperty("--glass-opacity", `${glassOpacity}%`);
    if (glassOpacity === 100) {
      style.setProperty("--glass-blur", "0px");
    } else {
      style.removeProperty("--glass-blur");
    }
  }, [glassOpacity]);

  return null;
}

function FontAppearanceSync() {
  const [advancedTypography] = useLocalStorage(
    TYPOGRAPHY_ADVANCED_STORAGE_KEY,
    false,
    Schema.Boolean,
  );
  const fontFamilySans = useClientSettings((settings) => settings.fontFamilySans);
  const fontFamilyCode = useClientSettings((settings) => settings.fontFamilyCode);
  const fontFamilyComposer = useClientSettings((settings) => settings.fontFamilyComposer);
  const fontFamilyTerminal = useClientSettings((settings) => settings.fontFamilyTerminal);
  const fontSizeInterface = useClientSettings((settings) => settings.fontSizeInterface);
  const fontSizePrompt = useClientSettings((settings) => settings.fontSizePrompt);
  const fontSizeCode = useClientSettings((settings) => settings.fontSizeCode);
  const fontSizeTerminal = useClientSettings((settings) => settings.fontSizeTerminal);
  const fontSmoothing = useClientSettings((settings) => settings.fontSmoothing);

  useEffect(() => {
    applyAppearanceFontVariables(document.documentElement, {
      sans: fontFamilySans,
      code: fontFamilyCode,
      composer: fontFamilyComposer,
      terminal: resolveTerminalFontPreference({
        advanced: advancedTypography,
        code: fontFamilyCode,
        terminal: fontFamilyTerminal,
      }),
      sizeInterface: fontSizeInterface,
      sizePrompt: fontSizePrompt,
      sizeCode: fontSizeCode,
      sizeTerminal: resolveTerminalFontSizePreference({
        advanced: advancedTypography,
        code: fontSizeCode,
        terminal: fontSizeTerminal,
      }),
      smoothing: fontSmoothing,
    });
  }, [
    advancedTypography,
    fontFamilyCode,
    fontFamilyComposer,
    fontFamilySans,
    fontFamilyTerminal,
    fontSizeCode,
    fontSizeInterface,
    fontSizePrompt,
    fontSizeTerminal,
    fontSmoothing,
  ]);

  return null;
}

/** Announces endings on threads the user is not watching. Renders nothing. */
function ThreadNotificationCoordinator() {
  useThreadNotifications();
  return null;
}

function DocumentTitleSync() {
  useEffect(() => {
    document.title = APP_DISPLAY_NAME;
  }, []);

  return null;
}

function RootRouteErrorView({ error, reset }: ErrorComponentProps) {
  return <AppErrorView error={error} onTryAgain={reset} />;
}

function EventRouter() {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (loc) => loc.pathname });
  const projectGroupingSettings = useClientSettings(selectProjectGroupingSettings);
  const primaryEnvironment = usePrimaryEnvironment();
  const openInEditor = useAtomCommand(shellEnvironment.openInEditor, {
    reportFailure: false,
  });
  const serverConfig = useAtomValue(primaryServerConfigAtom);
  const serverConfigEvent = useAtomValue(primaryServerConfigEventAtom);
  const serverWelcome = useAtomValue(primaryServerWelcomeAtom);
  const readPathname = useEffectEvent(() => pathname);
  const handledBootstrapThreadIdRef = useRef<string | null>(null);
  const handledConfigEventRef = useRef(serverConfigEvent);
  const [keybindingsToastController] = useState<KeybindingsUpdateToastController>(() =>
    createKeybindingsUpdateToastController({}),
  );

  const handleWelcome = useEffectEvent((payload: ServerLifecycleWelcomePayload | null) => {
    if (!payload) return;

    void (async () => {
      if (!payload.bootstrapProjectId || !payload.bootstrapThreadId) {
        return;
      }
      const bootstrapProject = readProject(
        scopeProjectRef(payload.environment.environmentId, payload.bootstrapProjectId),
      );
      const bootstrapProjectKey =
        (bootstrapProject
          ? deriveLogicalProjectKeyFromSettings(bootstrapProject, projectGroupingSettings)
          : null) ??
        (serverConfig?.cwd
          ? derivePhysicalProjectKeyFromPath(payload.environment.environmentId, serverConfig.cwd)
          : null) ??
        scopedProjectKey(
          scopeProjectRef(payload.environment.environmentId, payload.bootstrapProjectId),
        );
      useUiStateStore.getState().setProjectExpanded(bootstrapProjectKey, true);

      if (readPathname() !== "/") {
        return;
      }
      if (handledBootstrapThreadIdRef.current === payload.bootstrapThreadId) {
        return;
      }
      await navigate({
        to: "/$environmentId/$threadId",
        params: {
          environmentId: payload.environment.environmentId,
          threadId: payload.bootstrapThreadId,
        },
        replace: true,
      });
      handledBootstrapThreadIdRef.current = payload.bootstrapThreadId;
    })().catch(() => undefined);
  });

  const handleServerConfigUpdated = useEffectEvent(() => {
    const decision = keybindingsToastController.handle(serverConfigEvent);
    if (!decision) {
      return;
    }

    if (decision._tag === "Success") {
      toastManager.add({
        type: "success",
        title: "Keybindings updated",
        description: "Keybindings configuration reloaded successfully.",
      });
      return;
    }

    toastManager.add(
      stackedThreadToast({
        type: "warning",
        title: "Invalid keybindings configuration",
        description: decision.message,
        actionVariant: "outline",
        actionProps: {
          children: "Open keybindings.json",
          onClick: () => {
            if (!serverConfig || !primaryEnvironment) {
              return;
            }

            const editor = resolveAndPersistPreferredEditor(serverConfig.availableEditors);
            if (!editor) {
              return;
            }
            void (async () => {
              const result = await openInEditor({
                environmentId: primaryEnvironment.environmentId,
                input: {
                  cwd: serverConfig.keybindingsConfigPath,
                  editor,
                },
              });
              if (result._tag === "Success") {
                return;
              }
              const error = squashAtomCommandFailure(result);
              toastManager.add(
                stackedThreadToast({
                  type: "error",
                  title: "Unable to open keybindings file",
                  description: commandFailureMessage(error, "Unknown error opening file."),
                }),
              );
            })();
          },
        },
      }),
    );
  });

  useEffect(() => {
    handleWelcome(serverWelcome);
  }, [serverWelcome]);

  useEffect(() => {
    if (serverConfigEvent === null || handledConfigEventRef.current === serverConfigEvent) {
      return;
    }
    handledConfigEventRef.current = serverConfigEvent;
    handleServerConfigUpdated();
  }, [serverConfigEvent]);

  return null;
}
